/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2022 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author Shihan Rahim
 * 10/11/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import axios from 'axios';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";
import validator from "validator/es";

/**
 * HOC for the accounts form
 */
class ChangeCredentialsForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            userId: null,
            accountEmail: null,
            currentAccountKey: '',
            newAccountKey: '',
            confirmAccountKey: '',

            isDisplayLoader: true,
            errors: {},

            token: '',
            fields: [
                {
                    inputType: 'textPassword',
                    labelName: 'Old Password: *',
                    inputName: 'currentAccountKey',
                    placeholder: 'Please enter the old password: here',
                    value: '',
                    required: true
                },
                {
                    inputType: 'textPassword',
                    labelName: 'New Password: *',
                    inputName: 'newAccountKey',
                    placeholder: 'Please enter the new password here',
                    value: '',
                    required: true
                },
                {
                    inputType: 'textPassword',
                    labelName: 'Confirm Password: *',
                    inputName: 'confirmAccountKey',
                    placeholder: 'Please confirm the password here',
                    value: '',
                    required: true
                }
            ]
        };

        // set icons to the header
        EventEmitter.emit('setPageHeaderData', {
            page: 'changeCredentials',
            pageName: "Change Credentials",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/changeCredentials",
            optionalIcons: ['backIcon','clearFormIcon']
        });

        // set the event emitters
        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    // set a token to unsubscribe the async call
    CancelToken = axios.CancelToken;
    source = this.CancelToken.source();

    componentDidMount = async () => {
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);
        oldState.accountEmail = user.mail;
        oldState.token = token;
        oldState.isDisplayLoader = false;

        this.setState(oldState);
    };

    componentWillUnmount = () => {
        // remove all event emitters
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
        this.source.cancel("cancel change credentials async calls");
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else if (event.currentTarget !== undefined) {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    /**
     * handle the form submission
     */
    handleSubmit = () => {
        // data to be sent
        let data = {
            accountEmail: this.state.accountEmail,
            currentAccountKey: this.state.currentAccountKey,
            newAccountKey: this.state.newAccountKey
        };

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            this.changeCredentials(data);
        }
    };

    /**
     * validate the user inputs
     * @param key - input name
     * @param value - input value
     */
    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'accountEmail':
                if (value === '')
                    error[key] = "Email is Required!";
                else if (value === ' ')
                    error[key] = "Email can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "Email can't have more than 128 characters!";
                else
                    error[key] = "";
                break;
            case 'currentAccountKey':
                if (value === '')
                    error[key] = "Old Password is Required!";
                else if (value === ' ')
                    error[key] = "Old Password can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "Old Password can't have more than 128 characters!";
                else
                    error[key] = "";
                break;
            case 'newAccountKey':
                if (value === '')
                    error[key] = "Password is Required!";
                else if (value === ' ')
                    error[key] = "Password can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 64))
                    error[key] = "Password can't have more than 64 characters!";
                else
                    error[key] = "";
                break;
            case 'confirmAccountKey':
                if (value === '')
                    error[key] = "Please confirm the Password!";
                else if (value !== this.state.newAccountKey)
                    error[key] = "Password and Confirm Passwords do not match!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    /**
     * update accounts
     * @param data - updated Account details
     */
    changeCredentials = async (data) => {
        const newState = Object.assign({}, this.state);
        newState.isDisplayLoader = true;

        this.setState(newState);

        await API.put('/change-password',
            {
                "accountEmail": data.accountEmail,
                "currentAccountKey": data.currentAccountKey,
                "newAccountKey": data.newAccountKey
            },{headers: {Authorization: this.state.token}, cancelToken: this.source.token}
        ).then(response => {
            newState.isDisplayLoader = false;
            this.setState(newState);

            this.setAlert("Success", "Success", "Successfully updated the Credentials! Please Relogin!");

            setTimeout(() => {
                localStorage.removeItem('tmcUser')
                window.location.reload();
            }, 3000);
        }).catch(er => {
            newState.isDisplayLoader = false;
            this.setState(newState);
            this.displayErrorsForAPICalls(er);
        });
    };

    /**
     * display delete warning message
     */
    displayDeleteWarningMessage = () => {
        if (this.state.userId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'account',
                name: this.state.currentAccountKey
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Account Details",
                alertTitle: "Error"
            });
        }

    };

    /**
     * delete a accounts
     * @param data
     */
    deleteAccount = async (data) => {
        if (data && (this.props.match.path === "/home/accounts/:id")) {
            await API.delete('/account?userId=' + this.state.userId + '&email=' + this.state.currentAccountKey, {headers: {Authorization: this.state.token}, cancelToken: this.source.token})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the selected Account");
                    this.props.history.push('/home/accounts');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 401) {
            this.setAlert("Error", "Error", "Entered an invalid credentials. Please check!");
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    /**
     * fire an alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * clear the form
     */
    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.accountEmail = null;
        newState.currentAccountKey = '';
        newState.newAccountKey = '';
        newState.confirmAccountKey = '';
        newState.errors = {};

        this.setState(newState);
    };

    /**
     * navigate to previous page
     */
    navigateToPreviousPage = () => {
        this.props.history.push('/home');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default ChangeCredentialsForm;
